import type {
  MediaPlanChatMessage,
  MediaPlanWorkspaceConfigData,
  ResearchResultData,
  LinkData,
  MediaPlanPreviewData,
  CreateMediaPlanToolInput,
  CreateGoogleAdsMediaPlanToolInput,
  CreateMetaMediaPlanToolInput,
  SetEntityStatusToolInput,
  UpdateEntityToolInput,
  SetLineItemTargetingToolInput,
  EntityCardData
} from 'shared/src/api/ai-media-plan-chat-types';
import type { CampaignContextData } from 'shared/src/api/ai-line-item-chat-types';
import {
  BEESWAX_MEDIA_PLATFORM_ID,
  FACEBOOK_MEDIA_PLATFORM_ID,
  GOOGLE_SEARCH_MEDIA_PLATFORM_ID
} from 'shared/src/domain/media-platform/media-platforms';
import { formatMoney } from 'shared/src/utils/money/money-utils';

export function formatLifetimeBudget(value: string | undefined, budgetType: string): string {
  if (value == null || value === '') return '—';
  const n = parseFloat(value);
  if (Number.isNaN(n)) return value;
  return budgetType === 'impressions'
    ? `${Math.round(n).toLocaleString('en-US')} impressions`
    : formatMoney(n);
}

// The latest confirmed-write tool calls that drive the workspace cards. "Latest wins": the most recent
// non-streaming call of each kind is what renders.
export type LatestCreateCall = {
  toolCallId: string;
  tool: 'createMediaPlan' | 'createGoogleAdsMediaPlan' | 'createMetaMediaPlan';
  input:
    | CreateMediaPlanToolInput
    | CreateGoogleAdsMediaPlanToolInput
    | CreateMetaMediaPlanToolInput;
  state: string;
  output?: unknown;
};

export type LatestMutateCall = {
  toolCallId: string;
  tool: 'setEntityStatus' | 'updateEntity';
  input: SetEntityStatusToolInput | UpdateEntityToolInput;
  state: string;
  output?: unknown;
};

export type LatestTargetingCall = {
  toolCallId: string;
  input: SetLineItemTargetingToolInput;
  state: string;
  output?: unknown;
};

// The latest proposeGoogleAds READ-tool output. This is the deterministic source of truth for the
// Google Ads confirm card — it re-runs (new toolCallId) on every re-propose, whereas the
// createGoogleAdsMediaPlan tool INPUT only changes when the model chooses to re-emit that second
// call. The card binds its editable entities (and notices/summary) here so a chat "update the dates"
// re-propose refreshes the card even if the model never re-emits createGoogleAdsMediaPlan (BRVM-61).
export type LatestGoogleAdsProposal = {
  toolCallId: string;
  account_id: string;
  manager_account_id?: string;
  entities: CreateGoogleAdsMediaPlanToolInput['entities'];
  notices: MediaPlanPreviewData['notices'];
  summary: MediaPlanPreviewData['summary'];
};

// The latest proposeMeta READ-tool output — the Meta confirm card's source of truth, for the same
// reason as LatestGoogleAdsProposal (a re-propose refreshes the card without a new create call).
export type LatestMetaProposal = {
  toolCallId: string;
  account_id: string;
  page_id: string;
  entities: CreateMetaMediaPlanToolInput['entities'];
  notices: MediaPlanPreviewData['notices'];
  summary: MediaPlanPreviewData['summary'];
};

export type WorkspaceData = {
  campaignContext: CampaignContextData | null;
  workspaceConfig: MediaPlanWorkspaceConfigData | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  latestPreview: any;
  latestCreateCall: LatestCreateCall | null;
  latestMutateCall: LatestMutateCall | null;
  latestTargetingCall: LatestTargetingCall | null;
  latestGoogleAdsProposal: LatestGoogleAdsProposal | null;
  latestMetaProposal: LatestMetaProposal | null;
  latestResearch: ResearchResultData | null;
  // Emitting data-part ids for the research / entity cards — the render key, so a NEW research run
  // or lookup remounts its info panel (re-opening it) while re-renders keep the user's toggle.
  latestResearchId: string | null;
  latestEntityCard: EntityCardData | null;
  latestEntityCardId: string | null;
  links: LinkData[];
  // The agent called showConfigurationCard: a freeform session wants the configuration card, so
  // the workspace derives create mode from this instead of the (never-clicked) task chooser.
  configCardRequested: boolean;
  // The latest proposeGoogleAds output arrived AFTER the latest create call (a bare re-propose).
  // When that create call is already resolved, the receipt shows updated content and must reopen.
  proposalAfterCreate: boolean;
};

type MessagePart = MediaPlanChatMessage['parts'][number];

// One reducer per assistant message-part kind. This is the tool-keyed workspace card registry: adding a
// new tool/data part that should surface in the workspace means adding ONE entry here, instead of
// editing a hand-maintained god-loop. Each reducer folds the part into the accumulator with the same
// "latest wins" semantics the previous inline switch had.
const PART_REDUCERS: Record<string, (acc: WorkspaceData, part: MessagePart) => void> = {
  'tool-showConfigurationCard': (acc, part) => {
    if (part.type !== 'tool-showConfigurationCard' || part.state === 'input-streaming') return;
    acc.configCardRequested = true;
  },
  'data-campaign-context': (acc, part) => {
    if (part.type !== 'data-campaign-context') return;
    acc.campaignContext = part.data as CampaignContextData;
  },
  'data-workspace-config': (acc, part) => {
    if (part.type !== 'data-workspace-config') return;
    acc.workspaceConfig = part.data as MediaPlanWorkspaceConfigData;
  },
  'data-media-plan-preview': (acc, part) => {
    if (part.type !== 'data-media-plan-preview') return;
    acc.latestPreview = part.data;
  },
  // Beeswax confirmed-write: the latest createMediaPlan tool call drives the preview card. Skip
  // 'input-streaming' so the card doesn't flash with partially-streamed input.
  'tool-createMediaPlan': (acc, part) => {
    if (part.type !== 'tool-createMediaPlan' || part.state === 'input-streaming') return;
    const input = part.input as CreateMediaPlanToolInput | undefined;
    if (input?.line_item) {
      acc.latestCreateCall = {
        toolCallId: part.toolCallId,
        tool: 'createMediaPlan',
        input,
        state: part.state,
        output: part.output
      };
    }
  },
  // Google Ads confirmed-write: the resolved entities (from proposeGoogleAds) drive the card.
  'tool-createGoogleAdsMediaPlan': (acc, part) => {
    if (part.type !== 'tool-createGoogleAdsMediaPlan') return;
    // Cleared while the create input is still streaming too: a new card is on its way, so the
    // previous receipt must not flash open as "updated" in the meantime.
    acc.proposalAfterCreate = false;
    if (part.state === 'input-streaming') return;
    const input = part.input as CreateGoogleAdsMediaPlanToolInput | undefined;
    if (input?.entities) {
      acc.latestCreateCall = {
        toolCallId: part.toolCallId,
        tool: 'createGoogleAdsMediaPlan',
        input,
        state: part.state,
        output: part.output
      };
    }
  },
  // The card's entities, notices, and summary all come from the proposeGoogleAds READ tool's OUTPUT
  // (the source of truth) rather than depending on the model re-threading them into the create tool
  // input. This keeps the confirm card correct after any re-propose: dropped/unresolved geo targets
  // and other adjustments always show, and — the BRVM-61 fix — the editable dates/budget/name refresh
  // too, even when the model re-proposes without re-emitting createGoogleAdsMediaPlan.
  'tool-proposeGoogleAds': (acc, part) => {
    if (part.type !== 'tool-proposeGoogleAds' || part.state !== 'output-available') return;
    const output = part.output as
      | {
          account_id?: string;
          manager_account_id?: string;
          entities?: CreateGoogleAdsMediaPlanToolInput['entities'];
          notices?: MediaPlanPreviewData['notices'];
          summary?: MediaPlanPreviewData['summary'];
        }
      | undefined;
    if (!output?.entities?.length) return;
    acc.proposalAfterCreate = true;
    acc.latestGoogleAdsProposal = {
      toolCallId: part.toolCallId,
      account_id: output.account_id ?? '',
      manager_account_id: output.manager_account_id,
      entities: output.entities,
      notices: output.notices,
      summary: output.summary
    };
  },
  'tool-createMetaMediaPlan': (acc, part) => {
    if (part.type !== 'tool-createMetaMediaPlan' || part.state === 'input-streaming') return;
    const input = part.input as CreateMetaMediaPlanToolInput | undefined;
    if (input?.entities) {
      acc.latestCreateCall = {
        toolCallId: part.toolCallId,
        tool: 'createMetaMediaPlan',
        input,
        state: part.state,
        output: part.output
      };
    }
  },
  'tool-proposeMeta': (acc, part) => {
    if (part.type !== 'tool-proposeMeta' || part.state !== 'output-available') return;
    const output = part.output as Partial<CreateMetaMediaPlanToolInput> | undefined;
    if (!output?.entities?.length) return;
    acc.latestMetaProposal = {
      toolCallId: part.toolCallId,
      account_id: output.account_id ?? '',
      page_id: output.page_id ?? '',
      entities: output.entities,
      notices: output.notices,
      summary: output.summary
    };
  },
  // Management confirmed-write: the latest setEntityStatus call drives the generic mutate card.
  'tool-setEntityStatus': (acc, part) => {
    if (part.type !== 'tool-setEntityStatus' || part.state === 'input-streaming') return;
    const input = part.input as SetEntityStatusToolInput | undefined;
    if (input?.entity_id) {
      acc.latestMutateCall = {
        toolCallId: part.toolCallId,
        tool: 'setEntityStatus',
        input,
        state: part.state,
        output: part.output
      };
    }
  },
  'tool-updateEntity': (acc, part) => {
    if (part.type !== 'tool-updateEntity' || part.state === 'input-streaming') return;
    const input = part.input as UpdateEntityToolInput | undefined;
    if (input?.entity_id) {
      acc.latestMutateCall = {
        toolCallId: part.toolCallId,
        tool: 'updateEntity',
        input,
        state: part.state,
        output: part.output
      };
    }
  },
  'tool-setLineItemTargeting': (acc, part) => {
    if (part.type !== 'tool-setLineItemTargeting' || part.state === 'input-streaming') return;
    const input = part.input as SetLineItemTargetingToolInput | undefined;
    if (input?.line_item_id && input.targeting) {
      acc.latestTargetingCall = {
        toolCallId: part.toolCallId,
        input,
        state: part.state,
        output: part.output
      };
    }
  },
  'data-research-results': (acc, part) => {
    if (part.type !== 'data-research-results') return;
    acc.latestResearch = part.data as ResearchResultData;
    acc.latestResearchId = 'id' in part && part.id !== undefined ? String(part.id) : null;
  },
  'data-entity-card': (acc, part) => {
    if (part.type !== 'data-entity-card') return;
    acc.latestEntityCard = part.data as EntityCardData;
    acc.latestEntityCardId = 'id' in part && part.id !== undefined ? String(part.id) : null;
  },
  'data-link': (acc, part) => {
    if (part.type !== 'data-link') return;
    acc.links.push(part.data as LinkData);
  }
};

/** Walk every assistant message part and fold it into the workspace accumulator via the registry. */
export function extractWorkspaceData(messages: MediaPlanChatMessage[]): WorkspaceData {
  const acc: WorkspaceData = {
    campaignContext: null,
    workspaceConfig: null,
    latestPreview: null,
    latestCreateCall: null,
    latestMutateCall: null,
    latestTargetingCall: null,
    latestGoogleAdsProposal: null,
    latestMetaProposal: null,
    latestResearch: null,
    latestResearchId: null,
    latestEntityCard: null,
    latestEntityCardId: null,
    links: [],
    configCardRequested: false,
    proposalAfterCreate: false
  };

  for (const m of messages) {
    if (m.role !== 'assistant') continue;
    for (const part of m.parts) {
      PART_REDUCERS[part.type]?.(acc, part);
    }
  }

  return acc;
}

/** Build the preview-card data shape for the Google Ads confirm card.
 *
 * `toolCallId` stays the createGoogleAdsMediaPlan call id — that's the pending tool call the confirm
 * transport (googleAdsConfirmMediaPlan) resolves, and it must not change on a bare re-propose.
 *
 * The DISPLAYED entities/notices/summary, however, prefer the latest proposeGoogleAds OUTPUT (the
 * deterministic source that refreshes on every re-propose) over the create-tool INPUT the model
 * threads. Binding entities to the input was the BRVM-61 bug: after a chat "update the dates"
 * re-propose the model often doesn't re-emit createGoogleAdsMediaPlan, so the input — and the card —
 * stayed on the old dates while the notices (already sourced from the output) updated. Falls back to
 * the create input for the initial render before any propose output is captured. */
export function googleAdsProposalToPreview(
  input: CreateGoogleAdsMediaPlanToolInput,
  toolCallId: string,
  executionLineItemId: string,
  proposal: LatestGoogleAdsProposal | null
): MediaPlanPreviewData {
  return {
    request_id: toolCallId,
    execution_line_item_id: executionLineItemId,
    platform_id: GOOGLE_SEARCH_MEDIA_PLATFORM_ID,
    account_id: proposal?.account_id || input.account_id,
    manager_account_id: proposal?.manager_account_id ?? input.manager_account_id,
    entities: proposal?.entities ?? input.entities,
    notices: proposal?.notices ?? input.notices,
    summary: proposal?.summary ?? input.summary
  };
}

/** Build the preview-card data shape for the Meta confirm card — same source-of-truth rule as
 * googleAdsProposalToPreview: the create call id is what the confirm transport resolves, while the
 * displayed entities prefer the latest proposeMeta output. */
export function metaProposalToPreview(
  input: CreateMetaMediaPlanToolInput,
  toolCallId: string,
  executionLineItemId: string,
  proposal: LatestMetaProposal | null
): MediaPlanPreviewData {
  return {
    request_id: toolCallId,
    execution_line_item_id: executionLineItemId,
    platform_id: FACEBOOK_MEDIA_PLATFORM_ID,
    account_id: proposal?.account_id || input.account_id,
    entities: proposal?.entities ?? input.entities,
    notices: proposal?.notices ?? input.notices,
    summary: proposal?.summary ?? input.summary
  };
}

/** Build the preview-card data shape from a createMediaPlan tool input (Beeswax confirmed write). */
export function proposalToPreview(
  input: CreateMediaPlanToolInput,
  toolCallId: string,
  executionLineItemId: string
): MediaPlanPreviewData {
  const lineItemPayload = {
    ...input.line_item,
    campaign_id: input.campaign_mode === 'existing' ? input.campaign_id : 0
  };
  const entities =
    input.campaign_mode === 'create' && input.campaign_payload
      ? [
          { mode: 'create' as const, entityType: 'campaign', payload: input.campaign_payload },
          { mode: 'create' as const, entityType: 'line_item', payload: lineItemPayload }
        ]
      : [
          {
            mode: 'existing' as const,
            entityType: 'campaign',
            entity_id: String(input.campaign_id)
          },
          { mode: 'create' as const, entityType: 'line_item', payload: lineItemPayload }
        ];
  return {
    request_id: toolCallId,
    execution_line_item_id: executionLineItemId,
    platform_id: BEESWAX_MEDIA_PLATFORM_ID,
    account_id: input.account_id,
    entities,
    targeting: input.targeting
      ? { expression: input.targeting, summary: buildTargetingSummary(input.targeting) }
      : undefined,
    summary: buildBeeswaxProposalSummary(input)
  };
}

/** Client-derived review SUMMARY for the Beeswax card (value-only; Beeswax has no server propose
 * step that could attach reasoning/source — those stay platform-specific to Google Ads for now). */
function buildBeeswaxProposalSummary(
  input: CreateMediaPlanToolInput
): MediaPlanPreviewData['summary'] {
  const li = input.line_item;
  const summary: NonNullable<MediaPlanPreviewData['summary']> = [
    { label: 'Platform', value: 'Beeswax' },
    {
      label: 'Campaign',
      value:
        input.campaign_mode === 'create' && input.campaign_payload
          ? input.campaign_payload.name
          : `Existing campaign ${input.campaign_id}`
    },
    { label: 'Line item', value: `${li.name} (${li.type})` }
  ];
  if (li.spend_budget?.lifetime) {
    const formatted = formatLifetimeBudget(li.spend_budget.lifetime, li.budget_type);
    summary.push({
      label: 'Budget',
      value: li.budget_type === 'impressions' ? formatted : `${formatted} ${li.budget_type}`
    });
  }
  summary.push(
    { label: 'CPM bid', value: formatMoney(li.bidding.cpm_bid) },
    { label: 'Schedule', value: `${li.start_date} to ${li.end_date}` }
  );
  return summary;
}

function buildTargetingSummary(targeting: NonNullable<CreateMediaPlanToolInput['targeting']>) {
  const summary: Array<{
    group: string;
    key: string;
    values: Array<{ id: string; label?: string }>;
    reason?: string;
  }> = [];
  for (const [moduleKey, module] of Object.entries(targeting.modules)) {
    for (const [group, predicates] of Object.entries(module)) {
      if (!predicates || typeof predicates !== 'object') continue;
      for (const [targetingKey, predicate] of Object.entries(predicates)) {
        const values = extractPredicateEntries(predicate);
        const key = `${moduleKey}.${targetingKey}`;
        if (values.length > 0)
          summary.push({ group, key, values, reason: targeting.reasons?.[key] });
      }
    }
  }
  return summary;
}

function extractPredicateEntries(predicate: unknown): Array<{ id: string; label?: string }> {
  if (predicate === null || predicate === undefined) return [];
  if (typeof predicate !== 'object') return [{ id: String(predicate) }];
  if (Array.isArray(predicate)) return predicate.flatMap(p => extractPredicateEntries(p));
  const obj = predicate as Record<string, unknown>;
  if ('value' in obj) {
    const label = typeof obj.label === 'string' ? obj.label : undefined;
    return [{ id: String(obj.value), label }];
  }
  if ('values' in obj && Array.isArray(obj.values)) return obj.values.map(v => ({ id: String(v) }));
  if ('comparator' in obj) return [{ id: JSON.stringify(obj) }];
  return Object.values(obj).flatMap(v => extractPredicateEntries(v));
}
